// ============================================
// BrokerageMeetingRoom - Standalone meeting page
// Allows brokerage session participants to join
// a video call via the link in their email.
// No platform login required.
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useHMSActions, useHMSStore, selectRoomState, HMSRoomState } from '@100mslive/react-sdk';
import VideoRoom from './VideoRoom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BrokerageMeetingRoom = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const hmsRoomId = searchParams.get('hms');

  const [participantName, setParticipantName] = useState('');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLeft, setHasLeft] = useState(false);

  const hmsActions = useHMSActions();
  const roomState = useHMSStore(selectRoomState);

  // Clean up HMS on unmount
  useEffect(() => {
    return () => {
      if (roomState === HMSRoomState.Connected) {
        hmsActions.leave().catch(() => {});
      }
    };
  }, [roomState, hmsActions]);

  const joinMeeting = useCallback(async () => {
    if (!participantName.trim()) {
      setError('Please enter your name');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/video/meeting/${sessionId}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_name: participantName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Failed to join (${res.status})`);
      }
      const data = await res.json();
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, participantName]);

  const handleLeave = useCallback(() => {
    setToken(null);
    setHasLeft(true);
  }, []);

  // No session ID or HMS room ID
  if (!sessionId || !hmsRoomId) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Meeting Link</h1>
          <p className="text-gray-600">This meeting link appears to be invalid or incomplete. Please check the link in your email and try again.</p>
        </div>
      </div>
    );
  }

  // Left the meeting
  if (hasLeft) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Meeting Ended</h1>
          <p className="text-gray-600 mb-6">Thank you for joining. You can close this tab.</p>
          <button
            onClick={() => { setHasLeft(false); setToken(null); }}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Rejoin Meeting
          </button>
        </div>
      </div>
    );
  }

  // In the meeting
  if (token) {
    return (
      <div className="fixed inset-0 z-[999999] bg-gray-900">
        <VideoRoom
          token={token}
          roomId={hmsRoomId}
          roomType="brokerage_meeting"
          userName={participantName}
          calleeName=""
          onLeave={handleLeave}
          onEnd={handleLeave}
          onError={(err) => {
            console.error('VideoRoom error:', err);
            setError(err?.message || 'Video error occurred');
            setToken(null);
          }}
        />
      </div>
    );
  }

  // Lobby / join screen
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        {/* NACME branding */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900">NACME Introduction Meeting</h1>
          <p className="text-sm text-gray-500 mt-1">Enter your name to join the video call</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
            <input
              type="text"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && joinMeeting()}
              placeholder="Enter your full name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              autoFocus
            />
          </div>

          <button
            onClick={joinMeeting}
            disabled={loading || !participantName.trim()}
            className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Joining...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Join Meeting
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-6">
          Powered by NACME Searchlight
        </p>
      </div>
    </div>
  );
};

export default BrokerageMeetingRoom;
